import * as RadixTabs from '@radix-ui/react-tabs';
import { useHover } from '@react-aria/interactions';
import clsx from 'clsx';
import {
  RadixTriggerToReactAriaAdaptor,
  RadixTriggerToReactAriaAdaptorProps,
} from '@/utils/radixTriggerToReactAriaAdaptor';
import { makeElementClassNameFactory, makeRootClassName } from '../../utils';
import useSyncQueryParams from './hooks/useSyncQueryParams';
import type { ReactElement } from 'react';
import type {
  TabsContentProps as RadixTabsContentProps,
  TabsListProps as RadixTabListProps,
  TabsProps as RadixTabsProps,
  TabsTriggerProps as RadixTabsTriggerProps,
} from '@radix-ui/react-tabs';

export type TabsProps = RadixTabsProps;

export type TabProps = RadixTriggerToReactAriaAdaptorProps &
  RadixTabsTriggerProps;

export type TabListProps = RadixTabListProps;

export type TabContentProps = RadixTabsContentProps;

// config
// ------

const ROOT = makeRootClassName('Tabs');
const el = makeElementClassNameFactory(ROOT);

const DEFAULT_PROPS = {} as const;

function Root(props: TabsProps): ReactElement {
  const p = { ...DEFAULT_PROPS, ...props };
  const { children, className, ...otherProps } = p;

  return (
    <RadixTabs.Root
      {...otherProps}
      className={clsx(ROOT, className)}
      orientation="horizontal"
    >
      {children}
    </RadixTabs.Root>
  );
}

function QueryParamsSyncedRoot(
  props: TabsProps & {
    queryParamsKey: string;
  }
): ReactElement {
  const p = { ...DEFAULT_PROPS, ...props };
  const { children, className, queryParamsKey, ...otherProps } = p;

  const { tab, onTabChange } = useSyncQueryParams({
    key: queryParamsKey,
    defaultValue: otherProps.defaultValue,
  });

  return (
    <RadixTabs.Root
      {...otherProps}
      value={tab}
      onValueChange={onTabChange}
      className={clsx(ROOT, className)}
      orientation="horizontal"
    >
      {children}
    </RadixTabs.Root>
  );
}

function List(p: TabListProps): ReactElement {
  const { children, className, ...otherProps } = p;

  return (
    <RadixTabs.List {...otherProps} className={clsx(el`list-row`, className)}>
      {children}
    </RadixTabs.List>
  );
}

function Tab(p: TabProps): ReactElement {
  const { children, className, ...otherProps } = p;
  const { hoverProps, isHovered } = useHover({ isDisabled: p.disabled });

  return (
    <RadixTabs.Trigger
      {...hoverProps}
      {...otherProps}
      className={clsx(
        el`trigger`,
        {
          'is-disabled': p.disabled,
          'is-hovered': isHovered,
        },
        className
      )}
      disabled={p.disabled}
    >
      {p.hasReactAriaChildren ? (
        <RadixTriggerToReactAriaAdaptor>
          {p.children}
        </RadixTriggerToReactAriaAdaptor>
      ) : (
        p.children
      )}
    </RadixTabs.Trigger>
  );
}

function Content(props: TabContentProps): ReactElement {
  const { children, className, ...otherProps } = props;

  return (
    <RadixTabs.Content {...otherProps} className={clsx(el`content`, className)}>
      {children}
    </RadixTabs.Content>
  );
}

export default {
  Root,
  QueryParamsSyncedRoot,
  List,
  Tab,
  Content,
};
